import { Grid } from '@mui/material';
import MultistepForm from 'app/components/MultistepForm';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { RoleGeneral } from './RoleGeneral';
import { roleValidationSchema } from './validation';
import { useMutation } from '@apollo/client';

import { omit, omitBy, pick } from 'lodash';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { validateForm } from 'utils/FormErrorHelper';

import CREATE_ROLE_MUTATION from 'gql/mutations/PermissionProvider/createRole.gql';
import UPDATE_ROLE_MUTATION from 'gql/mutations/PermissionProvider/updateRole.gql';
import ENTITLEMENTS_QUERY from 'gql/queries/PermissionProvider/getEntitlements.gql';

interface Props {
  onCancel: Function;
  closeHandler: Function;
  setDisabledCancel?: Function;
  role?: any;
}

export function RoleModal(props: Props) {
  const [submitting, setSubmitting] = useState(false);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [createRole] = useMutation(CREATE_ROLE_MUTATION);
  const [updateRole] = useMutation(UPDATE_ROLE_MUTATION);

  const initialValues = () => {
    return props.role
      ? {
          ...pick(props.role, ['id', 'name', 'icon', 'active']),
          groupEntitlements: props.role.groupEntitlements.map(ent => ent.id)
        }
      : {
          active: true
        };
  };

  const handleSaveClick = async values => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(true);
    }
    const omitFields = ['__typename', 'id'];
    const saveBody: any = { ...omit(values, omitFields) };
    Object.assign(saveBody, {
      groupEntitlements: saveBody.groupEntitlements.map(ent => parseInt(ent, 10))
    });

    if (values.id) {
      try {
        await updateRole({
          variables: {
            id: values.id,
            data: omitBy(saveBody, v => v === null || v === '')
          },
          refetchQueries: ['GetRoles', { query: ENTITLEMENTS_QUERY }]
        });
        dispatch(
          AlertActions.success(
            t(translations.pages.managePermissionsSettings.alertsRole.saveSuccess())
          )
        );
      } catch (err) {
        dispatch(
          AlertActions.error(
            t(translations.pages.managePermissionsSettings.alertsRole.saveFailed())
          )
        );
      }
    } else {
      try {
        await createRole({
          variables: {
            data: omitBy(saveBody, v => v === null || v === '')
          },
          refetchQueries: ['GetRoles', { query: ENTITLEMENTS_QUERY }]
        });
        dispatch(
          AlertActions.success(
            t(translations.pages.managePermissionsSettings.alertsRole.saveSuccess())
          )
        );
      } catch (err) {
        dispatch(
          AlertActions.error(
            t(translations.pages.managePermissionsSettings.alertsRole.saveFailed())
          )
        );
      }
    }
    props.closeHandler();
    setSubmitting(false);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(false);
    }
  };

  const steps = ['Role Creation'];

  const RoleModalStep = ({ step }) => {
    switch (step) {
      case 0:
        return <RoleGeneral />;
      default:
        return null;
    }
  };

  return (
    <Formik
      isInitialValid={props.role ? true : false}
      initialValues={initialValues()}
      onSubmit={values => {
        handleSaveClick(values);
      }}
      validationSchema={yup.object().shape(roleValidationSchema)}
      validate={values => {
        return validateForm(values, yup.object().shape(roleValidationSchema), dispatch);
      }}
    >
      <MultistepForm submitting={submitting} steps={steps} onCancel={props.onCancel} hideStepper>
        {activeStep => (
          <Grid container className="role-detail-modal__body">
            <RoleModalStep step={activeStep} />
          </Grid>
        )}
      </MultistepForm>
    </Formik>
  );
}
